import {
  getTournamentDetailsRest,
  getTournamentPayoutTransactionsRest,
  getTournamentPlayersRest,
  getTournamentTablesRest,
  getTournamentEventsRest,
  getTournamentTotalChips,
  updateTournamentRest,
  getTableStatesRest,
} from '@/services/tournaments/tournaments-service';
import { TournamentUpdateInput } from '@repo/common-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const useTournamentDetails = (tournamentId: string) => {
  const queryClient = useQueryClient();

  const { data: tournament, refetch: refetchTournament } = useQuery({
    queryKey: ['tournament', tournamentId],
    queryFn: () => getTournamentDetailsRest(tournamentId),
  });

  const { data: players } = useQuery({
    queryKey: ['tournamentPlayers', tournamentId],
    queryFn: () => getTournamentPlayersRest(tournamentId),
  });

  const { data: transactions } = useQuery({
    queryKey: ['tournamentTransactions', tournamentId],
    queryFn: () => getTournamentPayoutTransactionsRest(tournamentId),
  });

  const {
    data: totalChips,
    isLoading: totalChipsLoading,
    refetch: refetchTotalChips,
  } = useQuery({
    queryKey: ['tournament-total-chips', tournamentId],
    queryFn: () => getTournamentTotalChips(tournamentId),
    enabled: !!tournamentId,
    staleTime: 30000, // Refresh every 30 seconds
  });

  const { data: tournamentTables, refetch: refetchTournamentTables } = useQuery({
    queryKey: ['tournamentTables', tournamentId],
    queryFn: () => getTournamentTablesRest(tournamentId),
  });

  const { data: tournamentEvents, refetch: refetchTournamentEvents } = useQuery({
    queryKey: ['tournamentEvents', tournamentId],
    queryFn: () => getTournamentEventsRest(tournamentId),
  });

  // Fetch table states from tournament server
  const { data: tableStates, refetch: refetchTableStates } = useQuery({
    queryKey: ['tournamentTableStates', tournamentId],
    queryFn: async () => {
      if (!tournament?.url) return null;
      try {
        const result = await getTableStatesRest(tournament.url);
        return result.success ? result.tables : null;
      } catch (error) {
        console.error('Failed to fetch table states:', error);
        return null;
      }
    },
    enabled: !!tournament?.url,
    staleTime: 10000, // Refresh every 10 seconds
    refetchInterval: 15000, // Auto-refresh every 15 seconds
  });

  // Merge table data with table states
  const mergedTournamentTables = tournamentTables?.map((table) => {
    const state = tableStates?.find((s) => s.tableId === table.tableId);
    const isActive = table.active ?? state?.isActive ?? true;

    return {
      ...table,
      table: {
        ...table.table,
        lastHandCompletedAt: state?.lastHandCompletedAt ?? null,
        isActive,
      },
    };
  });

  const { mutateAsync: updateTournament } = useMutation({
    mutationFn: (data: Partial<TournamentUpdateInput>) => updateTournamentRest(tournamentId, data),
    onSuccess: (updatedTournament) => {
      queryClient.setQueryData(['tournament', tournamentId], updatedTournament);
    },
  });

  return {
    tournament,
    players,
    transactions,
    totalChips,
    isLoading: totalChipsLoading,
    refetchTotalChips,
    refetchTournament,
    tournamentTables: mergedTournamentTables,
    refetchTournamentTables: async () => {
      await Promise.all([refetchTournamentTables(), refetchTableStates()]);
    },
    tournamentEvents,
    refetchTournamentEvents,
    updateTournament,
  };
};

export default useTournamentDetails;
